import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { MetricDetailsTab } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { getIndicatorClass, hashCode } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import type { CompareDisplayData } from 'webapp/components/block/content/common/compares';
import MetricDetailsTopDatapointsDatapointCardComponent from 'webapp/components/metric-details/top-datapoints/datapoint-card';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type { MetricSetup } from 'webapp/models-custom';
import type DateRange from 'webapp/models-custom/date-range';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

interface Args {
	metricSetup: MetricSetup;
	dateRange: DateRange | null;
}

export default class MetricDetailsTopDatapointsExploreForecastComponent extends Component<Args> {
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;

	@tracked isLoading = true;
	@tracked forecastData: APIData.MetricInsights.ForecastDataResponse | null = null;
	@tracked isFirstDayOfTheWeekError = false;
	@tracked validationErrorText = '';

	private paramHash = 0;
	private currentRequestId: string | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.requestInsightsForecastDataOnNext(false);

		this.eventManagerService.on(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this, () => {
			this.requestInsightsForecastDataOnNext(true);
		});
	}

	get isForecastingEnabled(): boolean {
		return this.metricDetailsService.showForecasts;
	}

	get label(): string {
		if (this.forecastData && !this.isLoading) {
			return `Forecast for ${this.forecastData.forecastLabel}`;
		}
		return 'Forecast';
	}

	get indicatorClass(): string {
		const changeValue = this.forecastData?.changeValue;
		return typeof changeValue === 'number'
			? getIndicatorClass(changeValue, this.args.metricSetup.metric?.changeInvert)
			: '';
	}

	get dateRangeFormulaId(): string {
		return this.args.dateRange?.formulaId ?? '';
	}

	get isError(): boolean {
		return this.isFirstDayOfTheWeekError || !!this.validationErrorText;
	}

	get errorText(): string {
		return this.isFirstDayOfTheWeekError
			? this.mmForecastsService.firstDayOfTheWeekErrorText
			: this.validationErrorText;
	}

	get forecastValueStr(): string | undefined {
		if (this.isError) {
			return 'Not available';
		}
		return this.forecastData?.forecastValueStr;
	}

	get isValueUnavailable(): boolean {
		return this.isError || !this.forecastData?.forecastValueStr;
	}

	get shouldHide(): boolean {
		return this.isForecastingEnabled && !this.isLoading && this.isValueUnavailable;
	}

	get comparePoints(): CompareDisplayData[] | undefined {
		if (!this.forecastData?.changeString) {
			return undefined;
		}
		return [
			{
				changeStr: this.forecastData.changeString,
				indicatorClass: this.indicatorClass,
				compareText: 'vs previous period',
			},
		];
	}

	get isForecastClickable(): boolean {
		return (
			this.isForecastingEnabled &&
			!this.isLoading &&
			!this.isError &&
			Boolean(this.forecastData)
		);
	}

	get forecastValuePoint(): DChartPrimaryPoint {
		return new DChartPrimaryPoint({
			valueStr: this.forecastValueStr,
			onClick: this.isForecastClickable ? this.openForecastTab : undefined,
		});
	}

	@action
	openForecastTab(): undefined {
		this.metricDetailsService.openMetricDetailsDrawer(
			{
				metricSetup: this.args.metricSetup,
				tab: MetricDetailsTab.FORECAST,
			},
			'explore-top-datapoint',
		);
		return undefined;
	}

	private setForecastData(data: {
		forecastData: APIData.MetricInsights.ForecastDataResponse | null;
		formulaId: string;
		isFirstDayOfTheWeekError?: boolean;
		validationErrorText?: string;
	}) {
		if (data.formulaId === this.dateRangeFormulaId) {
			this.forecastData = data.forecastData;
			this.isFirstDayOfTheWeekError = !!data.isFirstDayOfTheWeekError;
			this.validationErrorText = data.validationErrorText ?? '';
		}
	}

	private requestInsightsForecastData(forceNewRequest = false) {
		if (!this.isForecastingEnabled) {
			this.paramHash = 0;
			this.setForecastData({ forecastData: null, formulaId: this.dateRangeFormulaId });
			this.isLoading = false;
			return;
		}

		const metricSetup = this.args.metricSetup;
		const dateRange = this.args.dateRange;
		const formulaId = this.dateRangeFormulaId;

		if (!dateRange || dateRange.isToday || dateRange.isYesterday) {
			this.paramHash = 0;
			this.setForecastData({ forecastData: null, formulaId });
			this.isLoading = false;
			return;
		}

		const newParamHash = hashCode(
			JSON.stringify({
				metricSetupData: metricSetup.metricSetupData,
				dateRangeFormulaId: formulaId,
			}),
		);

		if (forceNewRequest || this.paramHash !== newParamHash) {
			this.isLoading = true;
			this.paramHash = newParamHash;

			if (this.currentRequestId) {
				this.mmForecastsService.unsubscribeFromInsightsForecastRequest(
					this.currentRequestId,
				);
			}

			const requestId = this.mmForecastsService.requestInsightsForecastData(
				dateRange,
				metricSetup,
				(forecastData) => {
					if (requestId !== this.currentRequestId) {
						return true;
					}
					this.setForecastData({ forecastData, formulaId });
					this.isLoading = false;
					this.currentRequestId = null;
					return true;
				},
				(errorData) => {
					if (requestId !== this.currentRequestId) {
						return;
					}
					const isFirstDayOfTheWeekError =
						this.mmForecastsService.getIsTheFirstDayOfTheWeekError(errorData);
					const validationErrorText =
						this.mmForecastsService.getValidationErrorText(errorData);

					this.setForecastData({
						forecastData: null,
						formulaId,
						isFirstDayOfTheWeekError,
						validationErrorText,
					});
					this.isLoading = false;
					this.currentRequestId = null;
				},
			);

			this.currentRequestId = requestId;
		}
	}

	@action
	requestInsightsForecastDataOnNext(forceNewRequest: boolean) {
		next(() => {
			this.requestInsightsForecastData(forceNewRequest);
		});
	}

	@action
	teardownView(): void {
		this.eventManagerService.off(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this);
	}

	override willDestroy() {
		super.willDestroy();
		if (this.currentRequestId) {
			this.mmForecastsService.unsubscribeFromInsightsForecastRequest(this.currentRequestId);
		}
	}

	<template>
		<div
			class="top-datapoints__forecast-wrapper
				{{if this.shouldHide 'top-datapoints__forecast-wrapper--hidden'}}"
			{{didUpdate
				(fn this.requestInsightsForecastDataOnNext false)
				@metricSetup.key
				@metricSetup.dimensions
				@metricSetup.filtersData
				@dateRange.formulaId
			}}
			{{willDestroy this.teardownView}}
		>
			{{#if this.isForecastingEnabled}}
				{{#unless this.shouldHide}}
					<MetricDetailsTopDatapointsDatapointCardComponent
						@label={{this.label}}
						@icon="Forecasts"
						@valueClass={{if this.isValueUnavailable "forecast-unavailable" "forecast"}}
						@valuePoint={{this.forecastValuePoint}}
						@comparePoints={{this.comparePoints}}
						@isLoading={{this.isLoading}}
					/>
					{{#if this.isError}}
						<div class="top-datapoints__card-error">
							<ToolTipWithIconComponent
								@text={{this.errorText}}
								@iconName="AlertFill"
								@maxWidth={{230}}
							/>
						</div>
					{{/if}}
				{{/unless}}
			{{/if}}
		</div>
	</template>
}
